// src/api/api.js
import axios from 'axios';
import { getAuthToken, getAdminUid } from './auth';
import { getAppCheckToken } from '../firebase/config';

const API_BASE_URL = process.env.NODE_ENV === 'development'
  ? process.env.REACT_APP_API_URL // Use the localhost URL from .env during development
  : ''; // Use the relative path /api in production for Firebase Hosting rewrite

// Now, when you make API calls, construct the full URL using API_BASE_URL
const API_URL = `${API_BASE_URL}/`;
// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle unauthorized errors (401)
    if (error.response && error.response.status === 401) {
      // Force logout or refresh token
      console.log('Unauthorized request');
      // Could trigger logout or token refresh here
    }
    return Promise.reject(error);
  }
);

// Add request interceptor for auth tokens
api.interceptors.request.use(async (config) => {
  // Get Firebase ID token
  const token = await getAuthToken();

  // App Check attests that this request came from the real app, not a script
  // replaying the API with a stolen ID token. Attached to every request,
  // signed-in or not - public browse endpoints are worth protecting from
  // scraping too. Resolves to null if App Check isn't configured or fails, in
  // which case the header is simply omitted rather than the request failing.
  const appCheckToken = await getAppCheckToken();
  if (appCheckToken) {
    config.headers['X-Firebase-AppCheck'] = appCheckToken;
  }

  console.log(`API Request to ${config.url}`, { 
    hasToken: !!token,
    isAdminRequest: window.location.href.indexOf("admin") > -1 || 
                    config.url.startsWith('/api/admins') || 
                    config.headers['X-Admin-Request']
  });
  
  if (token) {
    // Add Authorization header with token
    config.headers.Authorization = `Bearer ${token}`;
    
    // Check if the request is an admin API request
    if (window.location.href.indexOf("admin") > -1 || 
        config.url.startsWith('/api/admins') || 
        config.headers['X-Admin-Request']) {
      
      // Add admin UID to headers for admin-specific context
      const adminUid = getAdminUid();
      if (adminUid) {
        config.headers['X-Admin-UID'] = adminUid;
      }
    }
  } else {
    console.warn('No auth token available for request to:', config.url);
  }
  
  return config;
}, (error) => {
  console.error('Error in request interceptor:', error);
  return Promise.reject(error);
});

// User APIs
export const fetchUserProfile = async (userId) => {
  const response = await api.get(`/api/users/${userId}`);
  return response.data;
};

export const updateUserProfile = async (userId, userData) => {
  const response = await api.put(`/api/users/${userId}`, userData);
  return response.data;
};

export const addUserAddress = async (userId, addressData) => {
  const response = await api.post(`/api/users/${userId}/addresses`, addressData);
  return response.data;
};

export const updateUserAddress = async (userId, addressId, addressData) => {
  const response = await api.put(`/api/users/${userId}/addresses/${addressId}`, addressData);
  return response.data;
};

export const deleteUserAddress = async (userId, addressId) => {
  const response = await api.delete(`/api/users/${userId}/addresses/${addressId}`);
  return response.data;
};

export const fetchUserOrders = async (userId) => {
  const response = await api.get(`/api/users/${userId}/orders`);
  return response.data;
};

export const saveFcmToken = async (userId, token) => {
  const response = await api.post(`/api/users/${userId}/fcm-token`, { token });
  return response.data;
};

export const saveAdminFcmToken = async (adminId, token) => {
  const response = await api.post(`/api/admins/${adminId}/fcm-token`, { token });
  return response.data;
};

// Store APIs
export const fetchStores = async () => {
  const response = await api.get('/api/stores');
  return response.data;
};

export const fetchStore = async (storeId) => {
  const response = await api.get(`/api/stores/${storeId}`);
  return response.data;
};

// Category APIs
export const fetchCategories = async (storeId = null) => {
  const url = storeId ? `/api/categories?storeId=${storeId}` : '/api/categories';
  const response = await api.get(url);
  return response.data;
};

export const fetchCategory = async (categoryId) => {
  const response = await api.get(`/api/categories/${categoryId}`);
  return response.data;
};

// Product APIs
export const fetchProducts = async (categoryId = null, storeId = null) => {
  const params = new URLSearchParams();
  if (categoryId) params.set('categoryId', categoryId);
  if (storeId) params.set('storeId', storeId);
  const query = params.toString();
  const response = await api.get(`/api/products${query ? `?${query}` : ''}`);
  return response.data;
};

export const fetchProduct = async (productId) => {
  const response = await api.get(`/api/products/${productId}`);
  return response.data;
};

// Order APIs
export const createOrder = async (orderData) => {
  const response = await api.post('/api/orders', orderData);
  return response.data;
};

export const fetchOrder = async (orderId) => {
  const response = await api.get(`/api/orders/${orderId}`);
  return response.data;
};

export const fetchInventory = async (categoryId = null, storeId = null) => {
  const params = new URLSearchParams();
  if (categoryId) params.set('categoryId', categoryId);
  if (storeId) params.set('storeId', storeId);
  const query = params.toString();
  const response = await api.get(`/api/inventory/public${query ? `?${query}` : ''}`);
  return response.data;
};

export const getStoreSettings = async (storeId = null) => {
  const url = storeId ? `/api/settings?storeId=${storeId}` : '/api/settings';
  const response = await api.get(url);
  return response.data;
};

// Home APIs
export const fetchHomeSpecialties = async () => {
  const response = await api.get('/api/home/specialties');
  return response.data;
};

export const fetchHomeSuggestions = async () => {
  const response = await api.get('/api/home/suggestions');
  return response.data;
};

// Cross-store discovery. Both of these span stores, so neither takes (or
// defaults to) a storeId the way the single-store browse endpoints do.
// `pincode` is optional - passing the customer's default delivery zip limits
// results to stores that actually deliver to them; omitting it shows every
// active store, which is what a logged-out first-time visitor should see.
export const fetchDiscover = async ({ pincode = null, limit = null } = {}) => {
  const params = new URLSearchParams();
  if (pincode) params.set('pincode', pincode);
  if (limit) params.set('limit', limit);
  const query = params.toString();
  const response = await api.get(`/api/home/discover${query ? `?${query}` : ''}`);
  return response.data;
};

export const searchProducts = async (query, { pincode = null, storeId = null, limit = null } = {}) => {
  const params = new URLSearchParams({ q: query });
  if (pincode) params.set('pincode', pincode);
  if (storeId) params.set('storeId', storeId);
  if (limit) params.set('limit', limit);
  const response = await api.get(`/api/search/products?${params.toString()}`);
  return response.data;
};

// Star ratings. All three require a signed-in customer, and the backend only
// accepts a rating for a product that customer has actually ordered.
export const fetchMyProductRating = async (productId) => {
  const response = await api.get(`/api/products/${productId}/review`);
  return response.data;
};

export const rateProduct = async (productId, stars) => {
  const response = await api.put(`/api/products/${productId}/review`, { stars });
  return response.data;
};

export const removeProductRating = async (productId) => {
  const response = await api.delete(`/api/products/${productId}/review`);
  return response.data;
};

export default api;